import { FormArray, FormBuilder, Validators } from "react-reactive-form";

/**
 * Calculate initial Comparative PD block IDs from saved data.
 * Uses "Comp"-suffixed controls so it never conflicts with Standard PD.
 */
export const calculateInitialCompPdBlockIds = wizardForm => {
  // Detect from actual controls first
  const compPdTableControls = Object.keys(wizardForm.controls).filter(key =>
    /^qPdTableComp(_\d+)?$/.test(key)
  );
  if (compPdTableControls.length > 0) {
    const ids = compPdTableControls.map(key => {
      const match = key.match(/^qPdTableComp_(\d+)$/);
      return match ? parseInt(match[1]) : 1;
    });
    return [...new Set(ids)].sort((a, b) => a - b);
  }
  // Fallback: tracking array
  const formValues = wizardForm.value;
  if (formValues.qPdCompAnalyses && Array.isArray(formValues.qPdCompAnalyses)) {
    const len = Math.max(1, formValues.qPdCompAnalyses.length);
    return Array.from({ length: len }, (_, i) => i + 1);
  }
  return [1];
};

/**
 * Add a new Comparative PD block with the given block ID.
 */
export const addCompPdBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";

  const controlsToEnsure = [
    {
      name: `qPdTableComp${suffix}`,
      factory: () => FormBuilder.control("", Validators.required),
    },
    {
      name: `qPdPopulationComp${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPdComparativeComp${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    {
      name: `qPdCompNameComp${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPdSummaryTypeComp${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    {
      name: `qPdParametersComp${suffix}`,
      factory: () => new FormArray([]),
    },
    {
      name: `qPdTreatmentGroupsComp${suffix}`,
      factory: () => new FormArray([]),
    },
    {
      name: `qPdComparisonPairsComp${suffix}`,
      factory: () => new FormArray([]),
    },
    {
      name: `qPdDoseInteractionComp${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    {
      name: `qPdTimepointsComp${suffix}`,
      factory: () => new FormArray([]),
    },
    {
      name: `qPdStatTablesComp${suffix}`,
      factory: () => FormBuilder.control(""),
    },
  ];

  controlsToEnsure.forEach(({ name, factory }) => {
    if (!wizardForm.contains(name)) {
      wizardForm.addControl(name, factory());
    } else if (
      wizardForm.get(name) instanceof FormArray &&
      wizardForm.get(name).length > 0
    ) {
      while (wizardForm.get(name).length > 0) {
        wizardForm.get(name).removeAt(0);
      }
    }
  });
};

/**
 * Duplicate an existing Comparative PD block into a new block ID.
 */
export const duplicateCompPdBlock = (wizardForm, sourceBlockId, newBlockId) => {
  addCompPdBlock(wizardForm, newBlockId);

  const sourceSuffix = sourceBlockId > 1 ? `_${sourceBlockId}` : "";
  const newSuffix = newBlockId > 1 ? `_${newBlockId}` : "";

  const scalarControls = [
    "qPdTableComp",
    "qPdPopulationComp",
    "qPdComparativeComp",
    "qPdCompNameComp",
    "qPdSummaryTypeComp",
    "qPdDoseInteractionComp",
    "qPdStatTablesComp",
  ];

  scalarControls.forEach(baseName => {
    const sourceControl = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newControl = wizardForm.get(`${baseName}${newSuffix}`);
    if (!sourceControl || !newControl) return;
    const value = sourceControl.value;
    newControl.setValue(Array.isArray(value) ? [...value] : value);
  });

  const formArrayControls = [
    "qPdParametersComp",
    "qPdTreatmentGroupsComp",
    "qPdComparisonPairsComp",
    "qPdTimepointsComp",
  ];

  formArrayControls.forEach(baseName => {
    const sourceArray = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newArray = wizardForm.get(`${baseName}${newSuffix}`);
    if (!(sourceArray instanceof FormArray)) return;
    if (!(newArray instanceof FormArray)) return;
    while (newArray.length > 0) newArray.removeAt(0);
    sourceArray.controls.forEach((sourceGroup, index) => {
      const sourceValue = sourceGroup.value || {};
      const groupConfig = Object.keys(sourceValue).reduce((acc, key) => {
        acc[key] = [sourceValue[key] ?? ""];
        return acc;
      }, {});
      const newGroup = FormBuilder.group(groupConfig);
      newGroup.meta = { key: Date.now() + index };
      newArray.push(newGroup);
    });
  });
};

/**
 * Remove a Comparative PD block by its block ID.
 * Simply removes the block's controls — no data shifting needed.
 */
export const removeCompPdBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";
  const controlsToRemove = [
    `qPdTableComp${suffix}`,
    `qPdPopulationComp${suffix}`,
    `qPdComparativeComp${suffix}`,
    `qPdCompNameComp${suffix}`,
    `qPdSummaryTypeComp${suffix}`,
    `qPdParametersComp${suffix}`,
    `qPdTreatmentGroupsComp${suffix}`,
    `qPdComparisonPairsComp${suffix}`,
    `qPdDoseInteractionComp${suffix}`,
    `qPdTimepointsComp${suffix}`,
    `qPdStatTablesComp${suffix}`,
  ];

  controlsToRemove.forEach(name => {
    if (wizardForm.contains(name)) {
      wizardForm.removeControl(name);
    }
  });
};

/**
 * Calculate initial PD block IDs from saved data.
 * Returns a sorted array of block IDs.
 */
export const calculateInitialPdBlockIds = wizardForm => {
  // Detect from actual controls (excluding Comp-suffixed ones)
  const pdTableControls = Object.keys(wizardForm.controls).filter(key =>
    /^qPdTable(_\d+)?$/.test(key)
  );
  if (pdTableControls.length > 0) {
    const ids = pdTableControls.map(key => {
      const match = key.match(/^qPdTable_(\d+)$/);
      return match ? parseInt(match[1]) : 1;
    });
    return [...new Set(ids)].sort((a, b) => a - b);
  }
  // Fallback: tracking arrays (sequential on initial load)
  const formValues = wizardForm.value;
  const stdAnalyses = formValues.qPdStdAnalyses;
  const stdBlocks = Array.isArray(stdAnalyses)
    ? stdAnalyses
    : (stdAnalyses?.blocks ??
      (Array.isArray(formValues.qPdAnalyses) ? formValues.qPdAnalyses : null));
  if (stdBlocks && stdBlocks.length > 0) {
    const len = Math.max(1, stdBlocks.length);
    return Array.from({ length: len }, (_, i) => i + 1);
  }
  return [1];
};

/**
 * Add a new PD block with the given block ID.
 */
export const addPdBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";

  const controlsToEnsure = [
    {
      name: `qPdTable${suffix}`,
      factory: () => FormBuilder.control("", Validators.required),
    },
    {
      name: `qPdPopulation${suffix}`,
      factory: () => FormBuilder.control(""),
    },
    {
      name: `qPdComparative${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    { name: `qPdCompName${suffix}`, factory: () => FormBuilder.control("") },
    {
      name: `qPdSummaryType${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    { name: `qPdParameters${suffix}`, factory: () => new FormArray([]) },
    { name: `qPdTreatmentGroups${suffix}`, factory: () => new FormArray([]) },
    { name: `qPdComparisonPairs${suffix}`, factory: () => new FormArray([]) },
    {
      name: `qPdDoseInteraction${suffix}`,
      factory: () => FormBuilder.control("false"),
    },
    { name: `qPdColumnHeaders${suffix}`, factory: () => new FormArray([]) },
    { name: `qPdTimepoints${suffix}`, factory: () => new FormArray([]) },
    {
      name: `qPdStatTables${suffix}`,
      factory: () => FormBuilder.control(""),
    },
  ];

  controlsToEnsure.forEach(({ name, factory }) => {
    if (!wizardForm.contains(name)) {
      wizardForm.addControl(name, factory());
    } else if (
      wizardForm.get(name) instanceof FormArray &&
      wizardForm.get(name).length > 0
    ) {
      while (wizardForm.get(name).length > 0) {
        wizardForm.get(name).removeAt(0);
      }
    }
  });
};

/**
 * Duplicate an existing Standard PD block into a new block ID.
 */
export const duplicatePdBlock = (wizardForm, sourceBlockId, newBlockId) => {
  addPdBlock(wizardForm, newBlockId);

  const sourceSuffix = sourceBlockId > 1 ? `_${sourceBlockId}` : "";
  const newSuffix = newBlockId > 1 ? `_${newBlockId}` : "";

  const scalarControls = ["qPdTable", "qPdPopulation"];

  scalarControls.forEach(baseName => {
    const sourceControl = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newControl = wizardForm.get(`${baseName}${newSuffix}`);
    if (!sourceControl || !newControl) return;
    const value = sourceControl.value;
    newControl.setValue(Array.isArray(value) ? [...value] : value);
  });

  const formArrayControls = ["qPdColumnHeaders", "qPdTimepoints"];

  formArrayControls.forEach(baseName => {
    const sourceArray = wizardForm.get(`${baseName}${sourceSuffix}`);
    const newArray = wizardForm.get(`${baseName}${newSuffix}`);
    if (!(sourceArray instanceof FormArray)) return;
    if (!(newArray instanceof FormArray)) return;
    while (newArray.length > 0) newArray.removeAt(0);
    sourceArray.controls.forEach((sourceGroup, index) => {
      const sourceValue = sourceGroup.value || {};
      const groupConfig = Object.keys(sourceValue).reduce((acc, key) => {
        acc[key] = [sourceValue[key] ?? ""];
        return acc;
      }, {});
      const newGroup = FormBuilder.group(groupConfig);
      newGroup.meta = { key: Date.now() + index };
      newArray.push(newGroup);
    });
  });
};

/**
 * Remove a PD block by its block ID.
 * Simply removes the block's controls — no data shifting needed.
 */
export const removePdBlock = (wizardForm, blockId) => {
  const suffix = blockId > 1 ? `_${blockId}` : "";
  const controlsToRemove = [
    `qPdTable${suffix}`,
    `qPdPopulation${suffix}`,
    `qPdComparative${suffix}`,
    `qPdCompName${suffix}`,
    `qPdSummaryType${suffix}`,
    `qPdParameters${suffix}`,
    `qPdTreatmentGroups${suffix}`,
    `qPdComparisonPairs${suffix}`,
    `qPdDoseInteraction${suffix}`,
    `qPdColumnHeaders${suffix}`,
    `qPdTimepoints${suffix}`,
    `qPdStatTables${suffix}`,
  ];

  controlsToRemove.forEach(name => {
    if (wizardForm.contains(name)) {
      wizardForm.removeControl(name);
    }
  });
};
